'use client';

import { useFormContext } from 'react-hook-form';
import { getAriaLabel } from '@bridge/utils';
import { RadioGroupList } from '../../components';
import { DeclarativeFormField } from '../DeclarativeFormField';
import { FieldShell } from './FieldShell';
import { getFieldIds } from './utils';
import type { FieldValues, Path, PathValue } from 'react-hook-form';
import type { RadioOption } from '../../types';
import type { BaseComponentProps, BaseFieldProps } from './types';

// Define component-specific props extending BaseComponentProps
// Add back props specifically needed by RadioGroupList
export type RadioGroupListComponentProps = BaseComponentProps<typeof RadioGroupList> & {
  options?: RadioOption<string | boolean>[];
};

// Update main props interface to extend BaseFieldProps
interface RadioGroupListFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: RadioGroupListComponentProps;
}
export function RadioGroupListField<TFieldValues extends FieldValues>({
  field,
  fieldState,
  fieldConfig,
  componentProps,
  labelSize
}: RadioGroupListFieldProps<TFieldValues>): React.ReactElement {
  const {
    control,
    setValue
  } = useFormContext<TFieldValues>();
  const {
    id,
    options,
    disabled: componentDisabled,
    ...restComponentProps
  } = componentProps;
  const isDisabled = field.disabled || componentDisabled;
  const ids = getFieldIds(fieldConfig, fieldState, id);
  const optionPostfix = (option: RadioOption<string | boolean>): React.ReactNode | null => {
    if (componentProps.optionPostfix) {
      return componentProps.optionPostfix(option, field.value);
    }
    const stringifiedValue = field.value != null ? String(field.value) : undefined;
    const dependentFieldConfig = fieldConfig.dependentFields && stringifiedValue && stringifiedValue === String(option.value) ? fieldConfig.dependentFields[stringifiedValue] : undefined;
    return dependentFieldConfig ? <DeclarativeFormField control={control} fieldConfig={dependentFieldConfig} key={`${String(fieldConfig.name)}-dependent-${String(dependentFieldConfig.name)}`} labelSize={labelSize} /> : null;
  };
  const handleValueChange = (stringValue: string) => {
    const previousStringValue = field.value != null ? String(field.value) : undefined;
    // Clear the dependent field value if it's no longer the active one
    if (previousStringValue && fieldConfig.dependentFields?.[previousStringValue]) {
      const previousDependentFieldConfig = fieldConfig.dependentFields[previousStringValue];
      const currentDependentFieldConfig = fieldConfig.dependentFields?.[stringValue];
      if (previousDependentFieldConfig && (!currentDependentFieldConfig || previousDependentFieldConfig.name !== currentDependentFieldConfig.name)) {
        setValue(previousDependentFieldConfig.name as Path<TFieldValues>, undefined as PathValue<TFieldValues, Path<TFieldValues>>, {
          shouldValidate: true,
          shouldDirty: true
        });
      }
    }

    // Convert back to the original value type (Radix always gives us strings)
    const matchedOption = options?.find(opt => String(opt.value) === stringValue);
    field.onChange(matchedOption ? matchedOption.value : stringValue);
  };
  return <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize}>
      <RadioGroupList {...restComponentProps} aria-describedby={ids.describedBy} aria-invalid={ids.isInvalid} aria-label={getAriaLabel(fieldConfig.label)} disabled={isDisabled} id={ids.fieldId} labelSize={labelSize} onValueChange={handleValueChange} optionPostfix={optionPostfix} options={options || []} ref={field.ref} value={field.value != null ? String(field.value) : undefined} />
    </FieldShell>;
}